/**
 * The studio's two hooks. `.docs/20-studio-portal-pages.md` §Before fan-out 1, and
 * §DRY watchlist's first line: "`useStudioAction` is the only way a studio page calls
 * the API."
 *
 * `ARCHITECTURE.md` §API conventions: "The studio is RPC: every studio action is
 * `POST /api/studio/<action_name>` with the action's input schema as the body,
 * generated from the action registry." So there is no per-page hook file here the way
 * `api/client/` has one per route — there is one door with 158 names, and a page names
 * the one it wants.
 *
 * Four things these add over `useApiQuery` / `useApiMutation`, and nothing else:
 *
 *   1. **The key comes from the action's name.** `studioKey` puts the action's own
 *      module first (`api/keys.ts` `moduleOfAction`), so a write to `invoices_charge$`
 *      invalidates `billing` and the money page's `billing_summary` read refetches
 *      without any page listing it.
 *   2. **A write carries `idempotency_key` — in the body, not a header.**
 *      `apps/api/src/actions/define.ts` adds the field to every mutating action's
 *      *input schema*; the studio door reads no `Idempotency-Key` header at all, and
 *      a money action without the field is refused with `IdempotencyKeyRequired`.
 *      (`.docs/20` §Before fan-out 1 says "an `Idempotency-Key`"; the field is where
 *      the API actually wants it, and the report records the difference.) A key for
 *      each press — and, for a form, the key of its own submit made again where that
 *      one had no answer (`StudioActionOptions` `again`): the door answers the second
 *      with what became of the first.
 *   3. **`needs_review` is a result, not an error.** A `$` action over an agent's
 *      money limit answers `202` with `{ status, task_ref, reason }`
 *      (`packages/shared` `NeedsReviewResultSchema`). `useApiMutation` hands that back
 *      as data, so `reviewOf(result)` is how a page tells the two apart and
 *      `NeedsReviewNotice` is how it says so.
 *   4. **`alsoInvalidates` is a table, not a per-call argument.** Every studio page is
 *      a cross-module composition — start a retainer and the MRR chart, the clients
 *      book and the money page all move — and a page that had to remember which is a
 *      page that will forget. `api/keys.ts` `alsoInvalidatedBy` is the table.
 *
 * ```ts
 * const money = useStudioRead('billing_summary', { period })
 * const rerun = useStudioAction('billing_run')
 * await rerun.mutateAsync({ client: 'casaturca', period })
 * ```
 */

import type {
  QueryClient,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query'

import { hashKey } from '@tanstack/react-query'

import { isNeedsReview, type NeedsReviewResult } from '@fareast/shared'

import type { ApiClient } from './client.js'
import { useApiMutation, useApiPages, useApiQuery, type ApiError, type ApiPages } from './hooks.js'
import { alsoInvalidatedBy, moduleOfAction, queryKeys, studioKey, studioPagesKey } from './keys.js'

/* ------------------------------------------------------------------- the names */

/** The studio client, as `hc<StudioType>` builds it. */
type StudioClient = ApiClient['studio']

/**
 * Every action name, from the API's own union.
 *
 * `StudioSchema` is `{ [A in DefinedAction as `/${A['name']}`]: … }`, and `hc` turns
 * each path segment into a property — so the keys of this object *are* the catalogue,
 * typed. A page that misspells `billing_sumary` does not compile.
 *
 * `_catalogue` is a `$get` and is filtered out: it is the one thing under this prefix
 * that is not an action.
 */
export type StudioActionName = Extract<
  {
    [K in keyof StudioClient]: StudioClient[K] extends { $post: unknown } ? K : never
  }[keyof StudioClient],
  string
>

type PostOf<N extends StudioActionName> = StudioClient[N] extends { $post: infer P } ? P : never

/** What the action takes, as the RPC client declares it. Includes `idempotency_key`. */
export type StudioInput<N extends StudioActionName> =
  PostOf<N> extends (args: { json: infer J }, ...rest: never[]) => unknown ? J : never

type ResponseOf<N extends StudioActionName> =
  PostOf<N> extends (...args: never[]) => Promise<infer R> ? R : never

/** What it answers with, once the body is parsed. */
export type StudioOutput<N extends StudioActionName> =
  ResponseOf<N> extends { json: () => Promise<infer T> } ? T : never

/**
 * The answer, in the shape `useApiQuery` and `useApiMutation` read.
 *
 * `AnyResponse` with `json()` narrowed, rather than `hc`'s own `ClientResponse<N>`:
 * inside a function generic over every action, `ResponseOf<N>` is deferred and
 * `tsc` cannot see that it satisfies `AnyResponse` — so the constraint has to be
 * stated rather than inferred. The body type is still the action's own, which is what
 * `useApiQuery`'s `BodyOf<R>` reads to type the page's data.
 */
type StudioResponse<N extends StudioActionName> = {
  readonly ok: boolean
  readonly status: number
  readonly statusText: string
  json(): Promise<StudioOutput<N> | NeedsReviewResult>
}

/**
 * The input a caller writes: the action's own fields, without the one we add.
 *
 * A page never types `idempotency_key` — `useStudioAction` sends the one
 * `useApiMutation` makes, and a page that supplied its own would be deciding whether
 * its own retry is the same call, which is the decision the field exists to take away.
 */
export type StudioCallInput<N extends StudioActionName> = Omit<
  StudioInput<N>,
  typeof IDEMPOTENCY_FIELD
>

/**
 * The field `apps/api/src/actions/define.ts` adds to every mutating action's input.
 *
 * Repeated rather than imported for the reason `API_BASE_PATH` is: `apps/web` takes
 * **types only** from `@fareast/api`, and a string is not a type. The other statement
 * of it is `IDEMPOTENCY_FIELD` in that file, and what holds the two together is the
 * type: `StudioCallInput` takes this name off an input the API's own schema put it
 * on, and `studio.types.unit.test.ts` pins that it is gone from there — which it
 * would not be under another spelling.
 */
export const IDEMPOTENCY_FIELD = 'idempotency_key'

/**
 * A request body for an action whose **name is a value**.
 *
 * `StudioCallInput<N>` is the action's own fields, and it can only be that when `N`
 * is a literal. Two callers do not have one: `ActionForm` draws itself from
 * `_catalogue`'s published input schema, and the task detail's review panel replays
 * the arguments an agent already sent. In both, `N` is the whole union — and `Omit`
 * over a union of 158 object types is an object with no fields, which is what the
 * compiler reports as "unknown is not assignable to never".
 *
 * So the cast is not a body nobody checked. It is one checked by something the type
 * system cannot see, twice: `ActionForm` builds a zod schema from the very schema the
 * API published for that action and refuses a body that does not parse, the review
 * panel sends back arguments `execute()` already parsed once when the agent sent
 * them, and `execute()` parses again before it does anything either way.
 *
 * Everywhere else, the name is written out and the body is checked properly — which
 * it was not until session 20 found `defineAction` erasing its own input type.
 */
export function runtimeBody<N extends StudioActionName>(
  body: Readonly<Record<string, unknown>>,
): StudioCallInput<N> {
  return body as StudioCallInput<N>
}

/* ------------------------------------------------------------------- reading */

export type StudioReadOptions = {
  /** Render the failure in place instead of throwing it at the route boundary. */
  readonly inline?: boolean
  readonly enabled?: boolean
  readonly staleTime?: number
  /** Keep the last answer on screen while a changed input loads (`useApiQuery`). */
  readonly keepPrevious?: boolean
  /** Take a 4xx as the answer rather than retrying it (`useApiQuery`). */
  readonly refusalIsFinal?: boolean
  /** Ask again by itself, for a read other people change (`useApiQuery` `live`). */
  readonly live?: boolean
}

/**
 * One studio read. `POST /api/studio/<name>`, cached under the action's own module.
 *
 * Reads only in practice, but nothing here refuses a write: a `useStudioRead` on a
 * mutating action would run it on every render, which is a mistake `_catalogue`'s
 * `readOnly` flag cannot prevent at compile time. The naming is the guard, and
 * `pages/studio/rules.test.tsx` checks that every action a page *reads* is one the
 * catalogue marks read-only.
 */
export function useStudioRead<N extends StudioActionName>(
  name: N,
  input: StudioCallInput<N>,
  options: StudioReadOptions = {},
): UseQueryResult<StudioOutput<N>, ApiError> {
  return useApiQuery({
    key: studioKey(name, input as Record<string, unknown>),
    request: (api) => post(api, name, input as StudioInput<N>),
    ...options,
  }) as UseQueryResult<StudioOutput<N>, ApiError>
}

/** A studio action that answers a page and the cursor to the next one. */
type PagedAction = {
  [N in StudioActionName]: StudioOutput<N> extends { readonly next_cursor: string | null }
    ? N
    : never
}[StudioActionName]

/**
 * One studio read, **a page at a time** (`useApiPages`): the first page of `input`,
 * then `fetchNextPage` for each one after, following `next_cursor`.
 *
 * The input is the whole question and is sent unchanged on every page, with the
 * cursor added; the API refuses a cursor sent with a different one. Keyed by
 * `studioPagesKey`, so a write to the action's module refetches the pages loaded.
 */
export function useStudioPages<N extends PagedAction>(
  name: N,
  input: StudioCallInput<N>,
  options: {
    readonly enabled?: boolean
    readonly inline?: boolean
    /** Ask again by itself (`useApiPages` `live`). */
    readonly live?: boolean
  } = {},
): UseInfiniteQueryResult<ApiPages<StudioOutput<N>>, ApiError> {
  return useApiPages({
    key: studioPagesKey(name, input as Record<string, unknown>),
    request: (api, cursor) =>
      post(api, name, {
        ...(input as Record<string, unknown>),
        ...(cursor === undefined ? {} : { cursor }),
      } as StudioInput<N>),
    nextCursor: (page) => (page as { readonly next_cursor: string | null }).next_cursor,
    ...options,
  }) as UseInfiniteQueryResult<ApiPages<StudioOutput<N>>, ApiError>
}

/* ------------------------------------------------------------------- writing */

export type StudioActionOptions<N extends StudioActionName = StudioActionName> = {
  /** Extra modules this write disturbs, beyond the table in `api/keys.ts`. */
  readonly alsoInvalidates?: readonly Parameters<typeof useApiMutation>[0]['module'][]
  /**
   * For an action whose answer is the row a read shows: puts it in the cache, and a yes
   * reads nothing again (`useApiMutation` `answered`, which says who and why).
   */
  readonly answered?: (
    client: QueryClient,
    answer: StudioOutput<N> | NeedsReviewResult,
    variables: StudioCallInput<N>,
  ) => void
  /**
   * The task this call is made **from**, sent as `X-Task-Ref`.
   *
   * One caller: the task page's review panel, running the call a review task has been
   * holding. The header is what tells the API that this is that call rather than an
   * unrelated one of the same tool, and the API makes it once
   * (`apps/api/src/actions/define.ts` `ActionDeps.heldCall`).
   */
  readonly taskRef?: string
  /**
   * True for a control whose own press, **made again after one of the same that had
   * no answer, cannot be anything but that press**: a form, whose submit pressed again
   * as it stands sends the same thing again. It then goes out under the first one's
   * key (`useApiMutation` `identity`), and the door — which keeps its answer to a
   * key (`apps/api/src/actions/registry.ts` `runCall`) — answers with what became of
   * the first, making the call only if that one never arrived.
   *
   * Not for a button that is pressed again to do the thing again — "1 h", Claim: there
   * a second press is a second act, and under the first one's key it was answered
   * from the store and never made.
   */
  readonly again?: boolean
}

/**
 * The header `apps/api/src/actions/http.ts` reads the task a call is made under from.
 *
 * Repeated rather than imported for the reason `IDEMPOTENCY_FIELD` is: `apps/web`
 * takes types only from `@fareast/api`, and a string is not a type.
 */
export const TASK_REF_HEADER = 'X-Task-Ref'

/**
 * A studio write, as a component holds it: the cache's result, and `unsettled` — true
 * while this control holds the key of a press of its own that had no answer
 * (`api/hooks.ts` `ApiMutationResult`; only ever true where the control says `again`).
 */
export type StudioAction<N extends StudioActionName> = UseMutationResult<
  StudioOutput<N> | NeedsReviewResult,
  ApiError,
  StudioCallInput<N>
> & { readonly unsettled: boolean }

/**
 * One studio write.
 *
 * The answer is either the action's own output or a `needs_review` result; both are
 * `data`, because a 202 is a success. `reviewOf` is the discriminator.
 */
export function useStudioAction<N extends StudioActionName>(
  name: N,
  options: StudioActionOptions<N> = {},
): StudioAction<N> {
  return useApiMutation({
    module: moduleOfAction(name),
    alsoInvalidates: [...alsoInvalidatedBy(name), ...(options.alsoInvalidates ?? [])],
    /*
     * The answer is this action's — `request` below is what made it — but `BodyOf` over a
     * response generic in `N` is a conditional `tsc` leaves unresolved, so it is named here.
     */
    ...(options.answered === undefined
      ? {}
      : {
          answered: (client: QueryClient, answer: unknown, variables: StudioCallInput<N>) => {
            options.answered?.(client, answer as StudioOutput<N> | NeedsReviewResult, variables)
          },
        }),
    /*
     * An action's name and its input are the whole request — with the task it is made
     * from, where it is made from one. That is what makes a press the same press,
     * for a control that says its own is (`again`).
     */
    ...(options.again === true
      ? {
          identity: (variables: StudioCallInput<N>) =>
            hashKey([name, options.taskRef ?? null, variables]),
        }
      : {}),
    request: (api, variables: StudioCallInput<N>, _options, key) =>
      post(
        api,
        name,
        {
          ...(variables as Record<string, unknown>),
          /*
           * The press's key, in the body.
           *
           * `useApiMutation`'s own `money: true` adds an `Idempotency-Key` **header**,
           * which the client portal's REST routes read and the studio door does not —
           * so it is deliberately not used: it would put the key somewhere nothing
           * looks and leave the body's required field empty, and a `$` action would be
           * refused with `IdempotencyKeyRequired` on every click.
           */
          [IDEMPOTENCY_FIELD]: key,
        } as StudioInput<N>,
        options.taskRef === undefined ? undefined : { [TASK_REF_HEADER]: options.taskRef },
      ),
  })
}

/**
 * The review task an agent-limited `$` call was turned into, or null when the call
 * simply ran.
 *
 * `.docs/20` §Design decisions: "agent-prepared money actions are run by a human from
 * the task detail, never auto-run on review approval." This is the studio half of
 * that: a person's own click can also come back `needs_review` — a staff member has
 * no money limit, but a handler may raise one — and the page says so and links the
 * task rather than reporting success.
 */
export function reviewOf(result: unknown): NeedsReviewResult | null {
  return isNeedsReview(result) ? result : null
}

/* ----------------------------------------------------------------- catalogue */

/** One row of `GET /api/studio/_catalogue`, as the API declares it. */
export type CatalogueEntry = Awaited<
  ReturnType<Awaited<ReturnType<StudioClient['_catalogue']['$get']>>['json']>
>['actions'][number]

export type Catalogue = {
  readonly actions: readonly CatalogueEntry[]
  /** Every action's input as JSON Schema, by name. What `ActionForm` builds from. */
  readonly inputs: Readonly<Record<string, Record<string, unknown>>>
}

/**
 * How long the catalogue is good for: an hour.
 *
 * It changes when the server is deployed and not otherwise, and it is a quarter of a
 * megabyte of JSON Schema — so a page that opens three dialogs should fetch it once.
 * A deploy while somebody has the tab open means a stale form for at most an hour,
 * and a form built from a stale schema is refused by the server with the field-level
 * errors `ActionForm` already renders.
 */
const CATALOGUE_STALE_MS = 60 * 60 * 1000

/**
 * The tool list, with every input schema.
 *
 * The one read in the studio that is not an action: `_catalogue` is a `GET` under the
 * same prefix (`apps/api/src/actions/http.ts`), which is why it does not go through
 * `useStudioRead`.
 *
 * **`inline: true` on purpose.** `ActionForm` and `ReviewPanel` draw a Notice when
 * this fails — a blank New-task dialog must not take `/studio/tasks` down with it.
 * Without inline, TanStack throws at the route boundary and those Notices are dead
 * code.
 */
export function useCatalogue(): UseQueryResult<Catalogue, ApiError> {
  return useApiQuery({
    key: queryKeys.studio.catalogue(),
    request: (api) => api.studio._catalogue.$get({}),
    staleTime: CATALOGUE_STALE_MS,
    inline: true,
  })
}

/* ----------------------------------------------------------------- internals */

/**
 * The call itself.
 *
 * `hc`'s generated method is `(args, options?) => Promise<ClientResponse>`, and the
 * per-action type of `args` is what makes the union above impossible to call
 * generically — 158 signatures with 158 different `json` types have no common
 * supertype. The cast is here, once, in the one place that knows `name` and `input`
 * came from the same action.
 */
function post<N extends StudioActionName>(
  api: ApiClient,
  name: N,
  input: StudioInput<N>,
  headers?: Readonly<Record<string, string>>,
): Promise<StudioResponse<N>> {
  const call = api.studio[name] as unknown as {
    $post: (
      args: { json: unknown },
      options?: { headers: Readonly<Record<string, string>> },
    ) => Promise<StudioResponse<N>>
  }
  return headers === undefined
    ? call.$post({ json: input })
    : call.$post({ json: input }, { headers })
}
